// Copyright ©️ 2026 eVolve MEP, LLC

import type { UserId } from '@evolvemep/api-ts';

import { strongEntries } from 'helpers/strongEntries';

export const ssoAuthPath = 'login-microsoft-sso';
/** This is the full URL Sso will redirect back to after authentication */
export const ssoRedirectUri = `${window.location.origin}/auth/${ssoAuthPath}/` as const;

export type SsoRedirectState = Partial<{
  previousLoc: typeof window.location.pathname;
  userId: UserId;
  confirmationCode: string;
  /** The original `/desktop-auth` query string, when the SSO sign-in is for a desktop app. */
  desktopRequest: string;
}>;

export const buildSsoStateVar = (state: SsoRedirectState): string =>
  btoa(
    strongEntries(state)
      .map(([key, val]) => `${key}:${encodeURIComponent(String(val))}`)
      .join(','),
  );

const extractSsoStateVar = (stateString: string): SsoRedirectState => {
  try {
    return Object.fromEntries(
      atob(stateString)
        .split(',')
        .filter((v) => v.includes(':'))
        .map((v) => {
          const separatorIndex = v.indexOf(':');
          return [v.slice(0, separatorIndex), decodeURIComponent(v.slice(separatorIndex + 1))];
        }),
    ) as SsoRedirectState;
  } catch (e) {
    // oxlint-disable-next-line no-console
    console.error(e);
  }
  return {};
};

/** Reads the params Cognito appends (to the query or the hash) when it redirects back to {@link ssoRedirectUri}. */
export const readSsoCallbackParams = ({ search, hash }: Pick<Location, 'search' | 'hash'>) => {
  const params = new URLSearchParams(`${search}${hash}`.replace(/#/g, '?'));
  return {
    ssoCode: params.get('code'),
    state: extractSsoStateVar(params.get('state') ?? ''),
  };
};
